import { useEffect, useState } from 'react'
import { Screen } from '@/components/layout'
import { Button, InlineError } from '@/components/primitives'
import { api } from '@/lib/api'
import { t } from '@/lib/copy'
import { useNavigate, useRoute } from '@/lib/router'
import { useSession } from '@/lib/session'

type CollisionKind = 'emailMatch' | 'identityOwned' | 'providerSlot' | 'error'

export function AuthCollisionScreen() {
  const navigate = useNavigate()
  const route = useRoute()
  const { authenticated } = useSession()
  const result = route.query.get('result')
  const [kind, setKind] = useState<CollisionKind>(() => {
    if (result === 'error') return 'error'
    if (result === 'providerSlotOccupied') return 'providerSlot'
    if (result === 'identityOwned' || (authenticated && result === 'collision')) return 'identityOwned'
    return 'emailMatch'
  })

  // The Web callback intentionally exposes only `result=collision`. During an
  // authenticated Google-link attempt, the attached-identity list is enough
  // to distinguish a slot filled by another tab from an identity owned by a
  // different account, without putting either subject in the URL.
  useEffect(() => {
    if (!authenticated || result !== 'collision') return
    const controller = new AbortController()
    void api.auth.identities(controller.signal).then((identities) => {
      setKind(identities.some((identity) => identity.provider === 'google') ? 'providerSlot' : 'identityOwned')
    }).catch((error: unknown) => {
      if ((error as Error)?.name !== 'AbortError') setKind('error')
    })
    return () => controller.abort()
  }, [authenticated, result])

  const state = kind === 'emailMatch' ? 'emailMatch' : kind === 'error' ? 'error' : 'identityOwned'
  const title = kind === 'emailMatch' ? t('authCollision.email.title') : t('authCollision.identity.title')
  const body =
    kind === 'providerSlot'
      ? t('authCollision.providerSlot.body', { provider: t('signInMethods.provider.google') })
      : kind === 'identityOwned'
        ? t('authCollision.identity.body')
        : t('authCollision.email.body')
  const Heading = authenticated ? 'h2' : 'h1'

  return (
    <Screen id="authCollision" state={state} title={title} chrome={authenticated ? 'app' : 'standalone'}>
      <div className="state-block">
        <span className="beta-status-icon" aria-hidden="true">!</span>
        <Heading className="state-title">{title}</Heading>
        <p className="state-body">{body}</p>
        {state === 'error' ? <InlineError>{t('authCollision.error')}</InlineError> : null}
        <Button variant="primary" onClick={() => navigate(authenticated ? 'signInMethods' : 'signIn', {}, { replace: true })}>
          {authenticated ? t('authCollision.cta.methods') : t('authCollision.cta.signIn')}
        </Button>
        {kind === 'emailMatch' ? (
          <Button variant="quiet" onClick={() => navigate('privacy')}>
            {t('authCollision.cta.privacy')}
          </Button>
        ) : null}
      </div>
    </Screen>
  )
}
